/*
 * Whether marking a conversation done also closes the agent's work on it (LEA-225). Done is one
 * gesture for the inbox and the agent: where the agent still has something open, a draft, a
 * question or a draft still being written, Done records the plan's `no_response` outcome, which
 * closes it. Where nothing is open, or the plan cannot take an outcome, Done is the inbox's alone
 * and the reason is reported. A send already under way is never touched: it keeps reconciling.
 */
import type { WorkPlan } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";

export const CLOSURE_SKIPS = [
  "nothing_open",
  "already_closed",
  "booked",
  "sending",
  "held",
  "changed",
] as const;
export type ClosureSkip = (typeof CLOSURE_SKIPS)[number];

/** Why an Undo or a move back left a closure as it is. */
export const REOPEN_KEPT = ["new_message", "superseded", "changed"] as const;
export type ReopenKept = (typeof REOPEN_KEPT)[number];

const SETTLED: ReadonlySet<string> = new Set(["completed", "reported", "skipped"]);
const IN_FLIGHT: ReadonlySet<string> = new Set(["queued", "executing", "uncertain"]);

/**
 * Null when Done should close the plan's open work; otherwise why it leaves the agent alone. The
 * order follows what `set_operator_outcome` itself refuses, so a plan this lets through is one the
 * command accepts at the same revision. An active plan with no steps is a draft still being written:
 * closing it now keeps the draft closed when it lands, since the outcome holds for the same turn.
 */
export function closureSkip(plan: WorkPlan): ClosureSkip | null {
  if (plan.disposition !== "active") return "held";
  if (plan.goalCompletion) return "booked";
  if (plan.operatorOutcome) return "already_closed";
  if (plan.steps.some((step) => IN_FLIGHT.has(step.state))) return "sending";
  if (plan.steps.length > 0 && plan.steps.every((step) => SETTLED.has(step.state)))
    return "nothing_open";
  return null;
}
